import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Box,
} from '@mui/material';
import { Container } from '@mui/system';
import { HighlightOffOutlined } from '@mui/icons-material';
import { DropzoneArea } from 'mui-file-dropzone';
import DataField from 'src/components/DataField/DataField';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import { PortalRole, useListCustomersQuery, UserProfile } from 'src/backend';
import { Customer } from 'src/pages/Dashboard/CreateEditMachineGroupForm';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';

interface UploadMediaDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[], customerId: string | undefined) => void;
  isUploading: boolean;
}

function UploadMediaDialog({
  isOpen,
  onClose,
  onUpload,
  isUploading,
}: UploadMediaDialogProps) {
  const [files, setFiles] = useState<File[]>([]);
  const userProfile = useSelector(getUserProfile) as UserProfile;
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(undefined);
  const { data: customersData, isLoading: isLoadingCustomers } =
    useListCustomersQuery(false);

  const handleFileUpload = (uploadedFiles: File[]) => {
    setFiles(uploadedFiles);
  };

  const handleUpload = () => {
    if (!submitDisabled) {
      onUpload(files, selectedCustomerId);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setSelectedCustomerId('');
    onClose();
  };

  useEffect(() => {
    if (userProfile?.role === PortalRole[PortalRole.Admin]) {
      setSubmitDisabled(!selectedCustomerId || files.length === 0);
    } else {
      setSubmitDisabled(files.length === 0);
    }
  }, [files, selectedCustomerId, userProfile]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          height: '700px',
          width: '700px',
        },
      }}
    >
      <DialogTitle sx={{ fontSize: '1.5rem', padding: '20px' }}>
        Upload Media File
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <HighlightOffOutlined fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '20px' }}>
        <Grid container spacing={3}>
          <RoleBased
            requiredRole={PortalRole[PortalRole.Admin]}
            component={
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Customer *</InputLabel>
                  <Select
                    value={selectedCustomerId}
                    label="Customer *"
                    onChange={(e) => setSelectedCustomerId(e.target.value)}
                    disabled={isLoadingCustomers}
                  >
                    {customersData?.data?.map((customer: Customer) => (
                      <MenuItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            }
          />

          <Grid item xs={12}>
            <DropzoneArea
              fileObjects={[]}
              acceptedFiles={['.jpg', '.jpeg', '.mp4']}
              dropzoneText="Drag and drop media files here or click"
              onChange={handleFileUpload}
              showPreviews={false}
              showAlerts={false}
              filesLimit={10} // Allow up to 10 files
              maxFileSize={50000000} // 50 MB per file
            />
          </Grid>

          {files.length > 0 && (
            <Grid item xs={12}>
              <Container sx={{ marginTop: '10px' }}>
                <DataField
                  label="Selected Files"
                  value={`${files.length} file${
                    files.length === 1 ? '' : 's'
                  } selected`}
                />
                {files.map((file) => (
                  <Box
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                    }}
                  >
                    <DataField label="Name" value={file.name} />
                    <DataField
                      label="Size"
                      value={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                    <DataField label="Type" value={file.type} />
                  </Box>
                ))}
              </Container>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={isUploading}
          variant="contained"
          onClick={handleUpload}
          disabled={submitDisabled}
        >
          Upload Media
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default UploadMediaDialog;
